import { useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Tabs,
  Table,
  Timeline,
  Form,
  Input,
  Select,
  Modal,
  Spin,
  Empty,
  Tooltip,
  Progress,
  message,
} from 'antd'
import type { TabsProps, TableProps } from 'antd'
import {
  ArrowLeftOutlined,
  EditOutlined,
  SendOutlined,
  ReloadOutlined,
  TwitterOutlined,
  MailOutlined,
  GlobalOutlined,
  LinkOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  useProject,
  useUpdateProject,
  useRescoreProject,
  useOutreachRecords,
  useReplies,
  usePreviewOutreach,
  useSendOutreach,
} from '../hooks'
import type { OutreachRecord, Reply, ProjectStatus, OutreachChannel } from '../types'

// 状态配置
const statusConfig: Record<ProjectStatus, { color: string; text: string }> = {
  new: { color: 'default', text: '新项目' },
  scored: { color: 'blue', text: '已评分' },
  queued: { color: 'cyan', text: '待触达' },
  phase1_pending: { color: 'geekblue', text: 'P1待发' },
  phase1_sent: { color: 'purple', text: 'P1已发' },
  phase2_pending: { color: 'magenta', text: 'P2待发' },
  phase2_sent: { color: 'orange', text: 'P2已发' },
  phase3_pending: { color: 'gold', text: 'P3待发' },
  phase3_sent: { color: 'lime', text: 'P3已发' },
  replied: { color: 'green', text: '已回复' },
  interested: { color: 'success', text: '有意向' },
  meeting_scheduled: { color: 'processing', text: '已约会' },
  converted: { color: 'success', text: '已转化' },
  rejected: { color: 'error', text: '已拒绝' },
  cold_pool: { color: 'default', text: '冷池' },
}

const channelConfig: Record<OutreachChannel, { icon: React.ReactNode; color: string; text: string }> = {
  email: { icon: <MailOutlined />, color: 'blue', text: 'Email' },
  twitter_dm: { icon: <TwitterOutlined />, color: 'cyan', text: 'Twitter DM' },
  twitter_reply: { icon: <TwitterOutlined />, color: 'geekblue', text: 'Twitter Reply' },
  telegram_group: { icon: <SendOutlined />, color: 'purple', text: 'TG 群组消息' },
  telegram_dm: { icon: <SendOutlined />, color: 'volcano', text: 'TG DM' },
  telegram_admin_dm: { icon: <SendOutlined />, color: 'magenta', text: 'TG 管理员私信' },
  discord: { icon: <LinkOutlined />, color: 'magenta', text: 'Discord' },
}

function ProjectDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isEdit = searchParams.get('edit') === 'true'

  const [editModalOpen, setEditModalOpen] = useState(isEdit)
  const [outreachModalOpen, setOutreachModalOpen] = useState(false)
  const [previewContent, setPreviewContent] = useState<string>('')
  const [selectedChannel, setSelectedChannel] = useState<OutreachChannel>('email')
  const [form] = Form.useForm()
  const [outreachForm] = Form.useForm()

  const projectId = id || ''

  // 数据查询
  const { data: project, isLoading } = useProject(projectId)
  const { data: outreachData } = useOutreachRecords({ project_id: projectId })
  const { data: repliesData } = useReplies({ project_id: projectId })

  // Mutations
  const updateProject = useUpdateProject()
  const rescoreProject = useRescoreProject()
  const previewOutreach = usePreviewOutreach()
  const sendOutreach = useSendOutreach()

  // 触达记录列
  const outreachColumns: TableProps<OutreachRecord>['columns'] = [
    {
      title: '渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (channel: OutreachChannel) => {
        const config = channelConfig[channel]
        return (
          <Tag icon={config?.icon} color={config?.color}>
            {config?.text || channel}
          </Tag>
        )
      },
    },
    {
      title: '阶段',
      dataIndex: 'phase',
      key: 'phase',
      width: 80,
      render: (phase: number) => `Phase ${phase}`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          pending: { color: 'default', text: '待发送' },
          sent: { color: 'processing', text: '已发送' },
          delivered: { color: 'cyan', text: '已送达' },
          opened: { color: 'blue', text: '已打开' },
          clicked: { color: 'purple', text: '已点击' },
          replied: { color: 'success', text: '已回复' },
          bounced: { color: 'warning', text: '退信' },
          failed: { color: 'error', text: '失败' },
        }
        const config = statusMap[status]
        return <Tag color={config?.color}>{config?.text || status}</Tag>
      },
    },
    {
      title: '发送时间',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: 150,
      render: (time: string) => (time ? dayjs(time).format('MM-DD HH:mm') : '-'),
    },
    {
      title: '内容预览',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content: string) => (
        <Tooltip title={content}>
          {content?.substring(0, 50)}...
        </Tooltip>
      ),
    },
  ]

  // 回复记录列
  const replyColumns: TableProps<Reply>['columns'] = [
    {
      title: '渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (channel: OutreachChannel) => {
        const config = channelConfig[channel]
        return (
          <Tag icon={config?.icon} color={config?.color}>
            {config?.text || channel}
          </Tag>
        )
      },
    },
    {
      title: '意图',
      dataIndex: 'intent',
      key: 'intent',
      width: 100,
      render: (intent: string) => {
        const intentMap: Record<string, { color: string; text: string }> = {
          interested: { color: 'success', text: '有意向' },
          meeting_request: { color: 'processing', text: '约会议' },
          question: { color: 'blue', text: '有疑问' },
          not_interested: { color: 'default', text: '无意向' },
          auto_reply: { color: 'warning', text: '自动回复' },
          spam: { color: 'error', text: '垃圾' },
          unknown: { color: 'default', text: '未知' },
        }
        const config = intentMap[intent]
        return <Tag color={config?.color}>{config?.text || intent}</Tag>
      },
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (time: string) => dayjs(time).format('MM-DD HH:mm'),
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: '处理状态',
      dataIndex: 'is_processed',
      key: 'is_processed',
      width: 100,
      render: (processed: boolean) =>
        processed ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            已处理
          </Tag>
        ) : (
          <Tag icon={<ClockCircleOutlined />} color="warning">
            待处理
          </Tag>
        ),
    },
  ]

  // Tab配置
  const tabItems: TabsProps['items'] = [
    {
      key: 'outreach',
      label: `触达记录 (${outreachData?.total || 0})`,
      children: (
        <Table
          columns={outreachColumns}
          dataSource={outreachData?.items}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10 }}
        />
      ),
    },
    {
      key: 'replies',
      label: `回复记录 (${repliesData?.total || 0})`,
      children: (
        <Table
          columns={replyColumns}
          dataSource={repliesData?.items}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10 }}
        />
      ),
    },
    {
      key: 'timeline',
      label: '时间线',
      children: (
        <Timeline
          items={[
            ...(outreachData?.items?.map((o) => ({
              color: o.status === 'failed' ? 'red' : o.status === 'replied' ? 'green' : 'blue',
              children: (
                <div>
                  <div>
                    <Tag color={channelConfig[o.channel]?.color}>{channelConfig[o.channel]?.text}</Tag>
                    Phase {o.phase} - {o.status}
                  </div>
                  <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
                    {o.sent_at ? dayjs(o.sent_at).format('YYYY-MM-DD HH:mm') : '待发送'}
                  </div>
                </div>
              ),
            })) || []),
          ].sort((a, b) => 0)}
        />
      ),
    },
  ]

  // 预览触达内容
  const handlePreview = async () => {
    const values = outreachForm.getFieldsValue()
    try {
      const result = await previewOutreach.mutateAsync({
        project_id: projectId,
        channel: values.channel,
        phase: values.phase || (project?.phase || 0) + 1,
      })
      setPreviewContent(result.body)
    } catch (error) {
      // 错误已在API层处理
    }
  }

  // 发送触达
  const handleSendOutreach = async () => {
    const values = outreachForm.getFieldsValue()
    try {
      await sendOutreach.mutateAsync({
        project_id: projectId,
        channel: values.channel,
        phase: values.phase || (project?.phase || 0) + 1,
        content: previewContent || undefined,
      })
      setOutreachModalOpen(false)
      outreachForm.resetFields()
      setPreviewContent('')
    } catch (error) {
      // 错误已在API层处理
    }
  }

  // 更新项目
  const handleUpdate = async (values: Record<string, unknown>) => {
    try {
      await updateProject.mutateAsync({ id: projectId, data: values })
      setEditModalOpen(false)
    } catch (error) {
      // 错误已在API层处理
    }
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!project) {
    return <Empty description="项目不存在" />
  }

  return (
    <div className="fade-in">
      {/* 页面标题 */}
      <div className="page-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/projects')}>
            返回
          </Button>
          <h1 style={{ margin: 0 }}>{project.name}</h1>
          <Tag color={statusConfig[project.status]?.color}>
            {statusConfig[project.status]?.text}
          </Tag>
        </Space>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => rescoreProject.mutate(projectId)}
            loading={rescoreProject.isPending}
          >
            重新评分
          </Button>
          <Button icon={<EditOutlined />} onClick={() => setEditModalOpen(true)}>
            编辑
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => setOutreachModalOpen(true)}
          >
            发起触达
          </Button>
        </Space>
      </div>

      {/* 基本信息 */}
      <Card title="基本信息" style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="网站">
            <a href={project.website} target="_blank" rel="noopener noreferrer">
              <GlobalOutlined /> {project.website}
            </a>
          </Descriptions.Item>
          <Descriptions.Item label="Twitter">
            {project.twitter_handle ? (
              <a
                href={`https://twitter.com/${project.twitter_handle}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <TwitterOutlined /> @{project.twitter_handle}
              </a>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="邮箱">
            {project.email ? (
              <a href={`mailto:${project.email}`}>
                <MailOutlined /> {project.email}
              </a>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="分类">{project.category || '-'}</Descriptions.Item>
          <Descriptions.Item label="链">{project.chain || '-'}</Descriptions.Item>
          <Descriptions.Item label="TVL">
            {project.tvl ? `$${project.tvl.toLocaleString()}` : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="优先级分数">
            {project.priority_score ? (
              <Progress
                percent={project.priority_score}
                size="small"
                style={{ width: 150 }}
              />
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="当前阶段">Phase {project.phase || 0}</Descriptions.Item>
          <Descriptions.Item label="触达次数">{project.outreach_count || 0}</Descriptions.Item>
          <Descriptions.Item label="下次触达">
            {project.next_outreach_at
              ? dayjs(project.next_outreach_at).format('YYYY-MM-DD HH:mm')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="冷池次数">{project.cold_pool_count || 0}</Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {dayjs(project.created_at).format('YYYY-MM-DD HH:mm')}
          </Descriptions.Item>
        </Descriptions>
        {project.notes && (
          <div style={{ marginTop: 16 }}>
            <strong>备注：</strong>
            <p style={{ marginTop: 8, color: 'rgba(0,0,0,0.65)' }}>{project.notes}</p>
          </div>
        )}
      </Card>

      {/* 评分详情 */}
      {project.score_breakdown && (
        <Card title="评分详情" style={{ marginBottom: 16 }}>
          <Space wrap>
            {Object.entries(project.score_breakdown).map(([key, value]) => (
              <Tag key={key} color="blue">
                {key}: {value}
              </Tag>
            ))}
          </Space>
        </Card>
      )}

      {/* 触达和回复记录 */}
      <Card>
        <Tabs items={tabItems} />
      </Card>

      {/* 编辑弹窗 */}
      <Modal
        title="编辑项目"
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={updateProject.isPending}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            ...project,
            // telegram_admins: [{username, role}] → ['username1', 'username2']
            telegram_admins: (project?.telegram_admins || []).map((a: any) => a.username || a).filter(Boolean),
          }}
          onFinish={(values: any) => {
            // ['username1', 'username2'] → [{username, role, source}]
            if (values.telegram_admins && Array.isArray(values.telegram_admins)) {
              values.telegram_admins = values.telegram_admins.map((h: string) => ({
                username: h.replace(/^@/, '').trim(),
                role: 'admin',
                source: 'manual',
              }))
            }
            handleUpdate(values)
          }}
        >
          <Form.Item name="name" label="项目名称">
            <Input />
          </Form.Item>
          <Form.Item name="website" label="网站">
            <Input />
          </Form.Item>
          <Form.Item name="twitter_handle" label="Twitter">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="邮箱">
            <Input />
          </Form.Item>
          <Form.Item name="telegram_group" label="Telegram群组">
            <Input placeholder="https://t.me/group" />
          </Form.Item>
          <Form.Item name="telegram_admins" label="管理员 Telegram Handler">
            <Select
              mode="tags"
              placeholder="输入 handler（如 admin1），回车添加"
              tokenSeparators={[',', ' ', '\n']}
            />
          </Form.Item>
          <Form.Item name="category" label="分类">
            <Select
              options={[
                { value: 'DeFi', label: 'DeFi' },
                { value: 'NFT', label: 'NFT' },
                { value: 'GameFi', label: 'GameFi' },
                { value: 'Infrastructure', label: 'Infrastructure' },
                { value: 'DAO', label: 'DAO' },
                { value: 'Other', label: 'Other' },
              ]}
            />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select
              options={Object.entries(statusConfig).map(([key, value]) => ({
                value: key,
                label: value.text,
              }))}
            />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 触达弹窗 */}
      <Modal
        title="发起触达"
        open={outreachModalOpen}
        onCancel={() => {
          setOutreachModalOpen(false)
          outreachForm.resetFields()
          setPreviewContent('')
        }}
        footer={[
          <Button key="cancel" onClick={() => setOutreachModalOpen(false)}>
            取消
          </Button>,
          <Button key="preview" onClick={handlePreview} loading={previewOutreach.isPending}>
            预览
          </Button>,
          <Button
            key="send"
            type="primary"
            onClick={handleSendOutreach}
            loading={sendOutreach.isPending}
            disabled={!previewContent}
          >
            发送
          </Button>,
        ]}
        width={700}
      >
        <Form form={outreachForm} layout="vertical">
          <Form.Item
            name="channel"
            label="渠道"
            initialValue="email"
            rules={[{ required: true }]}
          >
            <Select
              options={Object.entries(channelConfig).map(([key, value]) => ({
                value: key,
                label: (
                  <Space>
                    {value.icon}
                    {value.text}
                  </Space>
                ),
              }))}
              onChange={(v) => {
                setSelectedChannel(v)
                setPreviewContent('')
              }}
            />
          </Form.Item>
          <Form.Item
            name="phase"
            label="阶段"
            initialValue={(project.phase || 0) + 1}
          >
            <Select
              options={[1, 2, 3].map((p) => ({ value: p, label: `Phase ${p}` }))}
            />
          </Form.Item>
        </Form>
        {previewContent && (
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>预览内容：</div>
            <Card size="small" style={{ background: '#fafafa' }}>
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{previewContent}</pre>
            </Card>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default ProjectDetail
